import { PlusIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import ProjectWorkspace from 'pages/AiProjects/ProjectWorkspace'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { AiProject, createAiProject, getAiProjects } from 'services'
import Api from 'services/api'
import { Button, Input2, Modal, Select2, TextArea } from 'stories/components'

/** Where the last project used on a loan is remembered, so reopening the tab lands where you left. */
const lastProjectKey = (loanNumber: string | number) => `loan-ai-project-${loanNumber}`

interface LoanAiProjectProps {
  /** Falls back to the loan the app currently has open, which is how every servicing tab reads it. */
  loanNumber?: number
}

/**
 * The AI Project workspace for the loan you are in: pick a project and the assistant works this
 * loan with that project's instructions and knowledge loaded, and its tools pointed at this loan.
 *
 * Conversations here belong to this loan, and artifacts the assistant saves are filed under it -
 * so one project (a servicing QC playbook, an investor reporting routine) can be run over many
 * loans without their work piling into one another.
 */
export default function LoanAiProject({ loanNumber: loanNumberProp }: LoanAiProjectProps) {
  const loanNumber = useMemo(() => Number(loanNumberProp || Api.getLoanNumber()) || 0, [loanNumberProp])

  const [isLoading, setLoading] = useState(true)
  const [projects, setProjects] = useState<AiProject[]>([])
  const [projectId, setProjectId] = useState<number>(0)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState({ name: '', description: '', instructions: '', isShared: false })

  useEffect(() => {
    getAiProjects()
      .then((data) => {
        const list = data || []
        setProjects(list)
        // Reopen the project last used on THIS loan; otherwise the most recently updated one, which
        // is what the list page shows first. Landing on a workspace beats landing on a chooser.
        const remembered = Number(localStorage.getItem(lastProjectKey(loanNumber)) || 0)
        const initial = list.find((project) => project.id === remembered)?.id || list[0]?.id || 0
        setProjectId(initial)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [loanNumber])

  const onSelectProject = (value: string) => {
    const id = Number(value) || 0
    setProjectId(id)
    if (id && loanNumber) localStorage.setItem(lastProjectKey(loanNumber), String(id))
  }

  const onCreate = async () => {
    if (!draft.name.trim()) {
      toast('Give the project a name.', { type: 'error' })
      return
    }
    setCreating(true)
    try {
      const project = await createAiProject(draft)
      setProjects((prev) => [project, ...prev])
      onSelectProject(String(project.id))
      setShowCreate(false)
      setDraft({ name: '', description: '', instructions: '', isShared: false })
    } finally {
      setCreating(false)
    }
  }

  const projectOptions = useMemo(
    () => projects.reduce((acc, project) => ({ ...acc, [project.id]: project.name }), {} as Record<string, string>),
    [projects],
  )

  return (
    <div className="LoanAiProject-container">
      <LayoutLoading show={isLoading} />

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="w-72 max-w-full">
          <Select2
            id="loan-ai-project"
            title="AI Project"
            options={projectOptions}
            value={projectId ? String(projectId) : ''}
            hasDefaultOption={false}
            onChange={onSelectProject}
          />
        </div>
        <Button size="sm" color="gray" onClick={() => setShowCreate(true)}>
          <span className="flex items-center gap-1.5">
            <PlusIcon className="w-4 h-4" />
            New project
          </span>
        </Button>
      </div>

      {!isLoading && !projects.length && (
        <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500">
          <SparklesIcon className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="font-semibold text-gray-700">No AI projects yet</p>
          <p className="text-sm mt-1">
            Create one for a recurring job - servicing QC, payoff review, investor reporting - give it the documents
            it should work from, then run it on this loan.
          </p>
          <div className="mt-4">
            <Button onClick={() => setShowCreate(true)}>New project</Button>
          </div>
        </div>
      )}

      {!!projectId && <ProjectWorkspace key={`${projectId}-${loanNumber}`} projectId={projectId} loanNumber={loanNumber} embedded />}

      {showCreate && (
        <Modal
          title="New AI Project"
          titleOkay="Create"
          isOpen
          loading={creating}
          onOk={onCreate}
          onClose={() => setShowCreate(false)}
        >
          <div className="w-96 max-w-full">
            <Input2
              title="Name"
              required
              value={draft.name}
              onChange={(value: string) => setDraft({ ...draft, name: value })}
            />
            <div className="mt-4">
              <Input2
                title="Description"
                value={draft.description}
                onChange={(value: string) => setDraft({ ...draft, description: value })}
              />
            </div>
            <div className="mt-4">
              <TextArea
                title="Project instructions"
                rows={5}
                value={draft.instructions}
                onChange={(value: string) => setDraft({ ...draft, instructions: value })}
              />
              <p className="text-xs text-gray-500 mt-1">
                How the assistant should behave in this project - what to always check, how to format its answer.
                Applies to every loan you run it on.
              </p>
            </div>
            <label className="flex items-center gap-2 mt-4 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={draft.isShared}
                onChange={(e) => setDraft({ ...draft, isShared: e.target.checked })}
              />
              Share with everyone in the company
            </label>
          </div>
        </Modal>
      )}
    </div>
  )
}
